/**
 * useProfilerSession — Full session lifecycle hook for the Data Profiler agent.
 *
 * Manages: source connection → SSE-streamed profiling → results display →
 *          DQ rule validation → export/handoff.
 *
 * Integrates:
 *   - profilerApi (REST calls to /api/v1/profiler/*)
 *   - useSSEStream (real-time progress via EventSource)
 *
 * All 34 modular components are driven by the state this hook exposes.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import {
  profilerApi,
  isDefaultExcludedTable,
  ProfilerApiError,
  type ProfileResults,
  type DQAssessment,
  type RelationshipResults,
  type TableProfile,
  type PreFlightReport,
  type DiscoveredTable,
  type UploadResult,
} from "../../../api/profiler-api";

import {
  useSSEStream,
  type SSEEvent,
  type SSEConnectionState,
} from "../../../hooks/useSSEStream";
import { sessionErrorOutcome } from "./sessionError";

// Re-export component types so the page doesn't need 15 import lines
export type { SSEConnectionState } from "../../../hooks/useSSEStream";
export type {
  ProfileResults,
  DQAssessment,
  RelationshipResults,
  TableProfile,
  DQIssue,
  PreFlightReport,
  DiscoveredFile,
  DiscoveredTable,
} from "../../../api/profiler-api";

/**
 * User-controllable profiling parameters surfaced in the table-picker step.
 * Mirrors the subset of /run request fields the picker exposes — other
 * flags (detect_pii, project_id) remain hardcoded for now.
 */
export interface RunConfig {
  /** Maximum rows to sample per table. `null` means "All rows". */
  sampleRows: number | null;
  includeDq: boolean;
  includeKeys: boolean;
  includeRelationships: boolean;
  /** Opt-in "Deep relationship discovery" (file/folder extracts only; ignored for databases). Compares values across
   *  tables to suggest relationships for columns whose names reveal no parent. Off by default. */
  includeDeepRelationships: boolean;
}

export const DEFAULT_RUN_CONFIG: RunConfig = {
  // 10k rows/table is statistically ample for column profiling, and DQ /
  // engagement rules re-query the full table regardless — so this cuts ingest
  // ~8x on remote/tunnelled sources with no loss of rule accuracy. Users can
  // still pick 100K / 1M / All in the table-select step for deeper stats.
  sampleRows: 10_000,
  includeDq: true,
  includeKeys: true,
  includeRelationships: true,
  includeDeepRelationships: false,
};

// ── Component-aligned types ─────────────────────────────────────────────────
// These match the prop interfaces of the 34 modular components exactly.

export type SessionStage = "connect" | "discovery" | "validate" | "share";
export type DetailTab =
  | "overview"
  | "schema"
  | "dq"
  | "keys"
  | "relations"
  | "ge";
// Mirrors ViewToggleBar's union — kept here so reducer dispatches against
// 'rules' | 'findings' | 'history' typecheck cleanly. The hook itself only
// drives navigation; per-view components live in the page.
export type ViewType =
  | "tables"
  | "relationships"
  | "summary"
  | "rules"
  | "findings"
  | "history";
export type SeverityLevel =
  | "excellent"
  | "good"
  | "moderate"
  | "poor"
  | "warning"
  | "critical";
export type SortField = "score" | "name" | "columns" | "rows";
export type SortDirection = "asc" | "desc";
export type DensityMode = "grid" | "list";
export type ProfilingPhase = "schema" | "statistics" | "analysis";

export interface SeverityCounts {
  excellent: number;
  good: number;
  moderate: number;
  poor: number;
  warning: number;
  critical: number;
}

export interface TableProgress {
  tableName: string;
  status: "profiling" | "complete" | "error";
  progress: number;
  phase: ProfilingPhase;
  startedAt: number;
  score?: number | null; // null = sampled, not yet scored (pending)
  severity?: SeverityLevel;
  columnCount?: number;
  rowCount?: number;
  error?: string;
  retryable?: boolean;
}

// ── Internal state ──────────────────────────────────────────────────────────

interface ProfilerState {
  // Lifecycle
  stage: SessionStage;
  internalPhase:
    | "empty"
    | "connecting"
    | "uploading"
    | "pre_flight_processing"
    | "pre_flight"
    | "table_select"
    | "profiling"
    | "loaded"
    | "error";
  sessionId: string | null;
  sourceName: string | null;
  sourceType: string | null;
  error: string | null;
  /** False for a terminal error the user can't fix by retrying (e.g. the
   *  secondary scanner flagged the file — retrying just 423s again). Mirrors
   *  TABLE_ERROR's existing `retryable` field. Defaults true. */
  errorRetryable: boolean;
  isLoading: boolean;

  /** Set while start_run's secondary (cloud-native) malware-scan gate is
   *  pending — distinct from `error`/internalPhase="error" because this is
   *  an expected, self-resolving wait (see pollSecondaryScanCleared), not a
   *  failure the user needs to act on. */
  scanPending: boolean;
  scanMessage: string | null;

  // Upload transfer progress (0-100), null when no upload is in flight.
  // Distinct from the pre-flight scan's own "processing" phase — this
  // covers only the browser→server byte transfer, which for a large
  // zip/folder is the dominant cost and previously had zero feedback
  // (fetch() has no upload-progress API at all).
  uploadProgress: number | null;

  // Multi-file pre-flight (FR5–FR8, DP-3.1–3.3)
  preFlightReport: PreFlightReport | null;
  selectedFileIds: Set<string>;

  // DB table-picker (PR #4) — populated after /db-connect, consumed by
  // TableSelectStep, cleared once the user clicks "Start Profile".
  discoveredTables: DiscoveredTable[];
  selectedTableNames: Set<string>;
  runConfig: RunConfig;

  // Schema switch (#682) — lets a user browse/switch schemas on the SAME
  // live connection instead of re-entering connection details for a
  // second schema. Only meaningful for source_type === "database".
  currentSchema: string | null;
  availableSchemas: string[];
  schemasLoaded: boolean;
  isSwitchingSchema: boolean;
  /** Scoped to the schema-switch dropdown — deliberately separate from
   *  `error` so a failed switch doesn't bounce the whole picker into the
   *  full-page error state (SET_ERROR flips internalPhase to "error"). */
  schemaSwitchError: string | null;

  // Profiling progress
  tablesTotal: number;
  tableProgress: Record<string, TableProgress>;

  // Poll-driven progress fallback (SSE-independent). The "N of M tables"
  // counter + climbing score are normally driven by SSE `table.profile_*`
  // events, but that stream can fail to deliver (proxy/middleware/connection
  // issues) leaving the counter pinned at 0 until the completion backfill.
  // The `/status` backstop poll now carries an incremental `tables_profiled`
  // + `aggregate_score` (derived server-side from the same event queue), and
  // these mirror them so the counter/score climb over the reliable fetch
  // channel even when SSE is silent. Monotonic — a late/stale poll response
  // can never regress them below what SSE already reported.
  polledTablesProfiled: number;
  polledAggregateScore: number;

  // Results
  results: ProfileResults | null;
  dqAssessment: DQAssessment | null;
  relationships: RelationshipResults | null;

  // UI state
  selectedTable: string | null;
  activeDetailTab: DetailTab;
  activeView: ViewType;
  searchQuery: string;
  activeSeverities: SeverityLevel[];
  sortField: SortField;
  sortDirection: SortDirection;
  density: DensityMode;

  // A11y
  announcement: string;
}

// ── Actions ─────────────────────────────────────────────────────────────────

type Action =
  | { type: "START_UPLOAD" }
  | { type: "UPLOAD_PROGRESS"; percent: number }
  | { type: "START_CONNECT" }
  | {
      type: "SESSION_CREATED";
      sessionId: string;
      sourceName: string;
      sourceType: string;
      tablesTotal: number;
    }
  | {
      type: "PRE_FLIGHT_PROCESSING";
      sessionId: string;
      sourceName: string;
    }
  | {
      type: "PRE_FLIGHT_READY";
      sessionId: string;
      sourceName: string;
      preFlightReport: PreFlightReport;
    }
  | { type: "TOGGLE_FILE"; fileId: string }
  | { type: "SET_ALL_FILES"; included: boolean }
  | { type: "FILE_SELECTION_CONFIRMED" }
  | {
      type: "TABLES_DISCOVERED";
      sessionId: string;
      sourceName: string;
      sourceType: string;
      tables: DiscoveredTable[];
      /** The schema this connection resolved to — distinct from
       *  `sourceName`, which (confusingly, pre-existing) also carries the
       *  schema name for DB sources. Kept separate here so #682's schema
       *  picker has an unambiguous field to read/compare against. */
      schema?: string;
    }
  | { type: "SCHEMAS_LOADED"; schemas: string[] }
  | { type: "SCHEMA_SWITCH_STARTED" }
  | {
      type: "SCHEMA_SWITCHED";
      schema: string;
      tables: DiscoveredTable[];
    }
  | { type: "SCHEMA_SWITCH_FAILED"; error: string }
  | { type: "TOGGLE_TABLE"; tableName: string }
  | { type: "SET_ALL_TABLES"; selected: boolean }
  | { type: "SET_RUN_CONFIG"; config: Partial<RunConfig> }
  | { type: "TABLE_SELECTION_CONFIRMED" }
  | { type: "PROFILING_STARTED" }
  | { type: "TABLE_START"; tableName: string }
  | {
      type: "TABLE_COMPLETE";
      tableName: string;
      score: number | null; // null = sampled but not yet scored (pending)
      severity: string;
      columnCount: number;
      rowCount: number;
    }
  | {
      type: "TABLE_ERROR";
      tableName: string;
      error: string;
      retryable: boolean;
    }
  | { type: "SESSION_COMPLETE"; aggregateScore: number; tablesProfiled: number }
  | {
      type: "RESULTS_LOADED";
      results: ProfileResults;
      dq: DQAssessment | null;
      relationships: RelationshipResults | null;
    }
  | { type: "SET_ERROR"; error: string; retryable?: boolean }
  | { type: "SCAN_PENDING"; message: string }
  | { type: "CLEAR_SESSION" }
  | { type: "SELECT_TABLE"; tableName: string | null }
  | { type: "SET_DETAIL_TAB"; tab: DetailTab }
  | { type: "SET_VIEW"; view: ViewType }
  | { type: "SET_SEARCH"; query: string }
  | { type: "TOGGLE_SEVERITY"; severity: SeverityLevel }
  | { type: "SET_SORTING"; field: SortField; direction: SortDirection }
  | { type: "SET_DENSITY"; mode: DensityMode }
  | { type: "SET_STAGE"; stage: SessionStage }
  | { type: "ANNOUNCE"; message: string }
  | {
      type: "POLL_PROGRESS";
      tablesProfiled: number;
      aggregateScore: number;
    }
  | { type: "TICK_PROGRESS" };

// ── Initial state ───────────────────────────────────────────────────────────

const INITIAL_STATE: ProfilerState = {
  stage: "connect",
  internalPhase: "empty",
  sessionId: null,
  sourceName: null,
  sourceType: null,
  error: null,
  errorRetryable: true,
  isLoading: false,
  scanPending: false,
  scanMessage: null,
  uploadProgress: null,

  preFlightReport: null,
  selectedFileIds: new Set(),

  discoveredTables: [],
  selectedTableNames: new Set(),
  runConfig: DEFAULT_RUN_CONFIG,

  currentSchema: null,
  availableSchemas: [],
  schemasLoaded: false,
  isSwitchingSchema: false,
  schemaSwitchError: null,

  tablesTotal: 0,
  tableProgress: {},
  polledTablesProfiled: 0,
  polledAggregateScore: 0,

  results: null,
  dqAssessment: null,
  relationships: null,

  selectedTable: null,
  activeDetailTab: "overview",
  // Default to Summary — users land on the aggregate health view rather
  // than the table grid, which is more useful as an opening snapshot.
  activeView: "summary",
  searchQuery: "",
  activeSeverities: [
    "excellent",
    "good",
    "moderate",
    "poor",
    "warning",
    "critical",
  ],
  sortField: "score",
  sortDirection: "asc",
  density: "grid",

  announcement: "",
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function severityFromScore(score: number): SeverityLevel {
  if (score >= 90) return "excellent";
  if (score >= 75) return "good";
  if (score >= 60) return "moderate";
  if (score >= 40) return "poor";
  return "critical";
}

function phaseFromProgress(progress: number): ProfilingPhase {
  if (progress < 33) return "schema";
  if (progress < 70) return "statistics";
  return "analysis";
}

const PREFLIGHT_POLL_INTERVAL_MS = 2000;
// A multi-GB archive's extract + scan is minutes, not seconds, but must
// still resolve — this is a generous ceiling, not an expected duration.
const PREFLIGHT_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/** Poll the background pre-flight job kicked off by uploadFolder/uploadFiles
 *  until it reports ready (returns the report) or failed (throws). */
async function pollFolderPreflight(
  sessionId: string,
): Promise<{ sourceName: string; preFlightReport: PreFlightReport }> {
  const deadline = Date.now() + PREFLIGHT_POLL_TIMEOUT_MS;
  for (;;) {
    const status = await profilerApi.getFolderPreflightStatus(sessionId);
    if (status.status === "ready" && status.pre_flight) {
      return {
        sourceName: status.source_name,
        preFlightReport: status.pre_flight,
      };
    }
    if (status.status === "failed") {
      throw new Error(status.error_message || "Pre-flight scan failed");
    }
    if (Date.now() >= deadline) {
      throw new Error("Pre-flight scan timed out");
    }
    await new Promise((resolve) =>
      setTimeout(resolve, PREFLIGHT_POLL_INTERVAL_MS),
    );
  }
}

// Exported for src/__tests__/useProfilerSession.startRunError.test.ts, which
// exercises handleStartRunError's branching directly (fake timers advance by
// this exact value rather than a hardcoded duplicate).
export const SECONDARY_SCAN_POLL_INTERVAL_MS = 5000;

/** Thrown by `pollSecondaryScanCleared` when the secondary scanner flags the
 *  file — a terminal outcome, never retried. */
class SecondaryScanFlaggedError extends Error {}

/** Poll start_run's secondary-scan verdict (via the read-only
 *  `/scan-status` endpoint, not start_run itself) until it clears. No time
 *  deadline: GuardDuty/Defender give no completion SLA, and unlike the
 *  pre-flight scan above (whose worst case is a huge archive we can bound),
 *  giving up on a timer here would strand the user on a dead end with no
 *  way to make progress — better to keep waiting quietly than to force a
 *  manual retry that's guaranteed to fail again immediately.
 *
 *  `shouldContinue` is checked before every poll (including the first) so
 *  the loop actually stops — rather than silently polling forever in the
 *  background — once the caller's session id has moved on (Start Over, a
 *  new upload, an SPA navigation with no full page reload all leave this
 *  async loop running with nothing else to cancel it). Returning here (not
 *  throwing) when abandoned is deliberate: the caller only resumes
 *  profiling if its own session-id check still matches, so a quiet return
 *  is a no-op for it either way.
 *
 *  Returns once the status is anything other than "pending" (cleared, or
 *  fail-open on timed_out/error — assert_secondary_scan_cleared treats
 *  both as pass-through); throws only for "infected", which start_run's
 *  own assert treats as a hard stop. */
async function pollSecondaryScanCleared(
  sessionId: string,
  shouldContinue: () => boolean,
): Promise<void> {
  while (shouldContinue()) {
    const { secondary_scan_status } =
      await profilerApi.getScanStatus(sessionId);
    if (secondary_scan_status === "infected") {
      throw new SecondaryScanFlaggedError(
        "This file was flagged by the secondary malware scan and has been quarantined for review.",
      );
    }
    if (secondary_scan_status !== "pending") return;
    await new Promise((resolve) =>
      setTimeout(resolve, SECONDARY_SCAN_POLL_INTERVAL_MS),
    );
  }
}

/** Shared catch-block handler for every `profilerApi.startRun()` call site
 *  (uploadFile, confirmFileSelection, confirmTableSelection, startProfiling
 *  each call startRun independently, with their own success-path dispatch
 *  logic that legitimately differs — this only centralizes the *error*
 *  branch, which must not drift apart between them the way it already did
 *  once: the original secondary-scan-gate fix only patched startProfiling's
 *  catch block, leaving these other three on the old generic error handling
 *  that shows a stale "please retry" banner with an active Retry button
 *  for a condition that's neither an error nor something Retry can fix).
 *
 *  `retryStartRun` re-invokes just the caller's startRun-and-dispatch-
 *  success step (not the whole outer function — re-running `uploadFile`
 *  itself here would re-upload the file).
 *
 *  Exported for src/__tests__/useProfilerSession.startRunError.test.ts —
 *  this branching logic already had to be patched once after shipping to
 *  only one of its four call sites, so it's worth a real regression test
 *  in isolation from React rather than trusting manual verification again. */
export async function handleStartRunError(
  err: unknown,
  sessionId: string,
  sessionIdRef: { current: string | null },
  dispatch: (action: Action) => void,
  retryStartRun: () => Promise<void>,
): Promise<void> {
  if (
    err instanceof ProfilerApiError &&
    err.code === "secondary_scan_pending"
  ) {
    dispatch({ type: "SCAN_PENDING", message: err.message });
    try {
      await pollSecondaryScanCleared(
        sessionId,
        () => sessionIdRef.current === sessionId,
      );
      // The scan cleared (or fail-opened) while we waited. Only resume if
      // the user hasn't since started over / uploaded something else out
      // from under this call.
      if (sessionIdRef.current === sessionId) {
        await retryStartRun();
      }
    } catch (pollErr) {
      if (sessionIdRef.current === sessionId) {
        dispatch({
          type: "SET_ERROR",
          error:
            pollErr instanceof Error
              ? pollErr.message
              : "Secondary malware scan failed",
          // A flagged file is a hard stop — retrying re-hits the same 423
          // forever. Any other rejection here (network hiccup reaching
          // /scan-status) stays retryable.
          retryable: !(pollErr instanceof SecondaryScanFlaggedError),
        });
      }
    }
    return;
  }
  if (
    err instanceof ProfilerApiError &&
    err.code === "secondary_scan_flagged"
  ) {
    // Can be hit directly (not just via the poll above) if the file was
    // already flagged before this call — e.g. resuming a session whose
    // scan resolved to "infected" between visits. Also a hard stop: no
    // Retry.
    dispatch({ type: "SET_ERROR", error: err.message, retryable: false });
    return;
  }
  dispatch({
    type: "SET_ERROR",
    error: err instanceof Error ? err.message : "Failed to start profiling",
  });
}

// ── Reducer ─────────────────────────────────────────────────────────────────

// Exported for tests only: the reducer is otherwise reachable only through the hook.
export { reducer as profilerReducer, INITIAL_STATE as PROFILER_INITIAL_STATE };

function reducer(state: ProfilerState, action: Action): ProfilerState {
  switch (action.type) {
    case "START_UPLOAD":
      return {
        ...state,
        internalPhase: "uploading",
        isLoading: true,
        error: null,
        uploadProgress: 0,
      };

    case "UPLOAD_PROGRESS":
      return { ...state, uploadProgress: action.percent };

    case "START_CONNECT":
      return {
        ...state,
        internalPhase: "connecting",
        isLoading: true,
        error: null,
        scanPending: false,
        scanMessage: null,
      };

    case "SESSION_CREATED":
      return {
        ...state,
        sessionId: action.sessionId,
        sourceName: action.sourceName,
        sourceType: action.sourceType,
        tablesTotal: action.tablesTotal,
        isLoading: false,
        uploadProgress: null,
        scanPending: false,
        scanMessage: null,
        announcement: `Source "${action.sourceName}" connected. ${action.tablesTotal} tables detected.`,
      };

    case "PRE_FLIGHT_PROCESSING":
      return {
        ...state,
        sessionId: action.sessionId,
        sourceName: action.sourceName,
        sourceType: "folder",
        internalPhase: "pre_flight_processing",
        isLoading: true,
        uploadProgress: null,
        announcement: `Uploaded "${action.sourceName}". Scanning files…`,
      };

    case "PRE_FLIGHT_READY": {
      // Build default selection: processable + encoding-ambiguous files
      // included. "encoding_ambiguous" is a soft warning, not a real
      // blocker — the backend's own pre-flight logic treats the two
      // statuses as the same usable tier (see DataProfilerPage.tsx's
      // FileRow for the fuller rationale); only unreadable/empty/
      // unsupported files are excluded by default.
      const defaultSelected = new Set<string>();
      for (const f of action.preFlightReport.files) {
        if (f.status === "processable" || f.status === "encoding_ambiguous") {
          defaultSelected.add(f.file_id);
        }
      }
      return {
        ...state,
        sessionId: action.sessionId,
        sourceName: action.sourceName,
        sourceType: "folder",
        internalPhase: "pre_flight",
        isLoading: false,
        preFlightReport: action.preFlightReport,
        selectedFileIds: defaultSelected,
        announcement: `Pre-flight complete: ${action.preFlightReport.processable_count} processable files out of ${action.preFlightReport.total_files}. Review and select files to profile.`,
      };
    }

    case "TOGGLE_FILE": {
      const next = new Set(state.selectedFileIds);
      if (next.has(action.fileId)) next.delete(action.fileId);
      else next.add(action.fileId);
      return { ...state, selectedFileIds: next };
    }

    case "SET_ALL_FILES": {
      if (!state.preFlightReport) return state;
      const next = new Set<string>();
      if (action.included) {
        for (const f of state.preFlightReport.files) {
          if (f.status === "processable" || f.status === "encoding_ambiguous") {
            next.add(f.file_id);
          }
        }
      }
      return { ...state, selectedFileIds: next };
    }

    case "FILE_SELECTION_CONFIRMED":
      return {
        ...state,
        isLoading: true,
        announcement: "Applying file selection…",
      };

    case "TABLES_DISCOVERED": {
      // Default to all tables selected EXCEPT views and Salesforce-sync
      // trigger/journal tables (see isDefaultExcludedTable) — those are
      // integration plumbing / unsized query objects, not profiling targets.
      // The picker stays a refinement: the user can re-tick any of them.
      const defaultSelected = new Set<string>(
        action.tables
          .filter((t) => t.name && !isDefaultExcludedTable(t))
          .map((t) => t.name),
      );
      return {
        ...state,
        sessionId: action.sessionId,
        sourceName: action.sourceName,
        sourceType: action.sourceType,
        tablesTotal: action.tables.length,
        internalPhase: "table_select",
        isLoading: false,
        discoveredTables: action.tables,
        selectedTableNames: defaultSelected,
        // Reset run config to defaults whenever a fresh DB connect lands.
        runConfig: DEFAULT_RUN_CONFIG,
        // A fresh connect always starts on ONE known schema; the full list
        // (for the #682 picker) is fetched separately once the table-select
        // step mounts.
        currentSchema: action.schema ?? action.sourceName ?? null,
        availableSchemas: [],
        schemasLoaded: false,
        announcement: `Connected to "${action.sourceName}". ${action.tables.length} tables discovered — choose which to profile.`,
      };
    }

    case "SCHEMAS_LOADED":
      return {
        ...state,
        availableSchemas: action.schemas,
        schemasLoaded: true,
      };

    case "SCHEMA_SWITCH_STARTED":
      return { ...state, isSwitchingSchema: true, schemaSwitchError: null };

    case "SCHEMA_SWITCHED": {
      const defaultSelected = new Set<string>(
        action.tables
          .filter((t) => t.name && !isDefaultExcludedTable(t))
          .map((t) => t.name),
      );
      return {
        ...state,
        // For a database source `sourceName` IS the schema (see TABLES_DISCOVERED's caller), and it is read as the
        // source's identity downstream (the "Connected to" header, the session label, the ignored-findings key), so it
        // must follow a switch rather than keep naming the schema the connection opened on.
        sourceName: action.schema,
        currentSchema: action.schema,
        discoveredTables: action.tables,
        selectedTableNames: defaultSelected,
        tablesTotal: action.tables.length,
        isSwitchingSchema: false,
        schemaSwitchError: null,
        announcement: `Switched to schema "${action.schema}". ${action.tables.length} tables discovered — choose which to profile.`,
      };
    }

    case "SCHEMA_SWITCH_FAILED":
      return {
        ...state,
        isSwitchingSchema: false,
        schemaSwitchError: action.error,
      };

    case "TOGGLE_TABLE": {
      const next = new Set(state.selectedTableNames);
      if (next.has(action.tableName)) next.delete(action.tableName);
      else next.add(action.tableName);
      return { ...state, selectedTableNames: next };
    }

    case "SET_ALL_TABLES": {
      const next = new Set<string>();
      if (action.selected) {
        for (const t of state.discoveredTables) {
          if (t.name) next.add(t.name);
        }
      }
      return { ...state, selectedTableNames: next };
    }

    case "SET_RUN_CONFIG":
      return {
        ...state,
        runConfig: { ...state.runConfig, ...action.config },
      };

    case "TABLE_SELECTION_CONFIRMED":
      return {
        ...state,
        isLoading: true,
        announcement: "Starting profile…",
      };

    case "PROFILING_STARTED":
      return {
        ...state,
        stage: "discovery",
        internalPhase: "profiling",
        isLoading: false,
        error: null,
        announcement: "Profiling started. Streaming results…",
      };

    case "TABLE_START": {
      const tp: TableProgress = {
        tableName: action.tableName,
        status: "profiling",
        progress: 5,
        phase: "schema",
        startedAt: Date.now(),
      };
      return {
        ...state,
        tableProgress: { ...state.tableProgress, [action.tableName]: tp },
        announcement: `Profiling table ${action.tableName}…`,
      };
    }

    case "TABLE_COMPLETE": {
      const existing = state.tableProgress[action.tableName];
      // Score and severity are DECOUPLED. The sampling pass now emits a
      // *preliminary* numeric score with severity "pending" so the top-line
      // quality score can climb table-by-table (the aggregateScore memo
      // includes any non-null score); the DQ pass later re-emits the same
      // table with its authoritative (score, severity), refining both.
      //
      // Severity is therefore set ONLY from a real (non-"pending") event
      // severity — never derived from the score. Deriving it from a
      // preliminary score would inject a phantom tier into the live Critical
      // badge before DQ has actually run. We keep the backend's authoritative
      // severity string (the SAME tiers _table_score_and_severity feeds the
      // final donut) so the badge converges exactly with no end-of-run jump;
      // until a real severity arrives we retain whatever was last set.
      const eventSev =
        action.severity && action.severity !== "pending"
          ? (action.severity as SeverityLevel)
          : undefined;
      const sev = eventSev ?? existing?.severity;
      const updated: TableProgress = {
        ...(existing || { tableName: action.tableName, startedAt: Date.now() }),
        status: "complete",
        progress: 100,
        phase: "analysis",
        score: action.score,
        severity: sev,
        columnCount: action.columnCount,
        rowCount: action.rowCount,
      };
      const completedCount = Object.values({
        ...state.tableProgress,
        [action.tableName]: updated,
      }).filter((t) => t.status === "complete").length;

      return {
        ...state,
        tableProgress: { ...state.tableProgress, [action.tableName]: updated },
        announcement: `${action.tableName} complete — score ${action.score}. ${completedCount} of ${state.tablesTotal} tables done.`,
      };
    }

    case "TABLE_ERROR": {
      const existing = state.tableProgress[action.tableName];
      const updated: TableProgress = {
        ...(existing || {
          tableName: action.tableName,
          startedAt: Date.now(),
          progress: 0,
          phase: "schema" as const,
        }),
        status: "error",
        error: action.error,
        retryable: action.retryable,
      };
      return {
        ...state,
        tableProgress: { ...state.tableProgress, [action.tableName]: updated },
        announcement: `Error profiling ${action.tableName}: ${action.error}`,
      };
    }

    case "SESSION_COMPLETE":
      return {
        ...state,
        internalPhase: "loaded",
        announcement: `Profiling complete. Aggregate score: ${action.aggregateScore}. ${action.tablesProfiled} tables profiled.`,
      };

    case "RESULTS_LOADED": {
      // Populate tableProgress from results so tablesComplete is accurate
      // (synchronous profiling skips SSE TABLE_COMPLETE events)
      const loadedProgress: Record<string, TableProgress> = {
        ...state.tableProgress,
      };
      for (const table of action.results.tables) {
        if (
          !loadedProgress[table.table_name] ||
          loadedProgress[table.table_name].status !== "complete"
        ) {
          loadedProgress[table.table_name] = {
            tableName: table.table_name,
            status: "complete",
            progress: 100,
            phase: "analysis",
            startedAt: Date.now(),
            score: table.score,
            severity: severityFromScore(table.score),
            columnCount: table.column_count,
            rowCount: table.row_count,
          };
        }
      }
      return {
        ...state,
        stage: "discovery",
        internalPhase: "loaded",
        results: action.results,
        dqAssessment: action.dq,
        relationships: action.relationships,
        tableProgress: loadedProgress,
        isLoading: false,
        tablesTotal: action.results.total_tables,
        announcement: `Results loaded. ${action.results.total_tables} tables, aggregate score ${action.results.aggregate_score}.`,
      };
    }

    case "SET_ERROR":
      return {
        ...state,
        error: action.error,
        errorRetryable: action.retryable ?? true,
        isLoading: false,
        internalPhase: "error",
        uploadProgress: null,
        scanPending: false,
        scanMessage: null,
      };

    case "SCAN_PENDING":
      return {
        ...state,
        scanPending: true,
        scanMessage: action.message,
        isLoading: false,
      };

    case "CLEAR_SESSION":
      return {
        ...INITIAL_STATE,
        selectedFileIds: new Set(),
        selectedTableNames: new Set(),
        discoveredTables: [],
        runConfig: DEFAULT_RUN_CONFIG,
      };

    case "SELECT_TABLE":
      return {
        ...state,
        selectedTable: action.tableName,
        activeDetailTab: "overview",
        announcement: action.tableName
          ? `Viewing details for ${action.tableName}`
          : "",
      };

    case "SET_DETAIL_TAB":
      return { ...state, activeDetailTab: action.tab };

    case "SET_VIEW":
      return { ...state, activeView: action.view };

    case "SET_SEARCH":
      return { ...state, searchQuery: action.query };

    case "TOGGLE_SEVERITY": {
      const current = state.activeSeverities;
      const next = current.includes(action.severity)
        ? current.filter((s) => s !== action.severity)
        : [...current, action.severity];
      return { ...state, activeSeverities: next.length > 0 ? next : current };
    }

    case "SET_SORTING":
      return {
        ...state,
        sortField: action.field,
        sortDirection: action.direction,
      };

    case "SET_DENSITY":
      return { ...state, density: action.mode };

    case "SET_STAGE":
      return { ...state, stage: action.stage };

    case "ANNOUNCE":
      return { ...state, announcement: action.message };

    case "POLL_PROGRESS": {
      // SSE-independent progress from the `/status` backstop poll. Clamp
      // monotonically so an out-of-order poll response can't make the counter
      // or score jump backwards; only advance when the poll reports more
      // progress than we've already shown (via SSE or a prior poll).
      const nextTables = Math.max(
        state.polledTablesProfiled,
        action.tablesProfiled,
      );
      const nextScore = Math.max(
        state.polledAggregateScore,
        action.aggregateScore,
      );
      if (
        nextTables === state.polledTablesProfiled &&
        nextScore === state.polledAggregateScore
      ) {
        return state;
      }
      return {
        ...state,
        polledTablesProfiled: nextTables,
        polledAggregateScore: nextScore,
      };
    }

    case "TICK_PROGRESS": {
      const now = Date.now();
      let changed = false;
      const updated = { ...state.tableProgress };

      for (const [name, tp] of Object.entries(updated)) {
        if (tp.status !== "profiling") continue;
        const elapsed = (now - tp.startedAt) / 1000;
        // Advance ~3% per second, cap at 95 (waits for server confirmation)
        const newProgress = Math.min(95, 5 + elapsed * 3);
        if (Math.abs(newProgress - tp.progress) > 0.5) {
          updated[name] = {
            ...tp,
            progress: newProgress,
            phase: phaseFromProgress(newProgress),
          };
          changed = true;
        }
      }

      return changed ? { ...state, tableProgress: updated } : state;
    }

    default:
      return state;
  }
}

// ── Hook options ────────────────────────────────────────────────────────────

export interface UseProfilerSessionOptions {
  projectId?: string;
  resumeSessionId?: string;
}

// ── Return type ─────────────────────────────────────────────────────────────

export interface UseProfilerSessionReturn {
  // Session lifecycle
  stage: SessionStage;
  internalPhase: ProfilerState["internalPhase"];
  sessionId: string | null;
  sourceName: string | null;
  sourceType: string | null;
  error: string | null;
  /** False for a terminal error a Retry can't fix (e.g. flagged by the
   *  secondary malware scanner). Defaults true for every other error. */
  errorRetryable: boolean;
  isLoading: boolean;
  /** True while start_run's secondary malware-scan gate is pending — the
   *  hook is auto-polling and will resume on its own; the UI should show a
   *  non-actionable status, not an error banner with a Retry button. */
  scanPending: boolean;
  scanMessage: string | null;
  /** Upload transfer progress (0-100), null when no upload is in flight. */
  uploadProgress: number | null;
  /** The saved project connection the current database session profiles. */
  profiledConnectionId: string | null;

  // Actions
  uploadFile: (file: File) => Promise<void>;
  uploadFiles: (files: File[]) => Promise<void>;
  uploadFolder: (file: File) => Promise<void>;
  /** Connect to a DB. Returns the new session_id on success, null on error. */
  connectDatabase: (config: Record<string, unknown>) => Promise<string | null>;
  /** Start profiling. Accepts an explicit sessionId to avoid stale closures. */
  startProfiling: (explicitSessionId?: string) => Promise<void>;
  clearSession: () => void;
  setStage: (stage: SessionStage) => void;

  // Multi-file pre-flight (FR5–FR8)
  preFlightReport: PreFlightReport | null;
  selectedFileIds: Set<string>;
  toggleFileSelection: (fileId: string) => void;
  setAllFilesSelected: (included: boolean) => void;
  confirmFileSelection: () => Promise<void>;

  // DB table-picker (PR #4)
  discoveredTables: DiscoveredTable[];
  selectedTableNames: Set<string>;
  toggleTableSelection: (tableName: string) => void;
  setAllTablesSelected: (selected: boolean) => void;
  runConfig: RunConfig;
  setRunConfig: (config: Partial<RunConfig>) => void;
  confirmTableSelection: () => Promise<boolean>;

  // Schema switch (#682) — browse/switch schemas on the same live
  // connection from the table-picker step, without a fresh db-connect.
  currentSchema: string | null;
  availableSchemas: string[];
  isSwitchingSchema: boolean;
  schemaSwitchError: string | null;
  switchSchema: (schemaName: string) => Promise<void>;

  // Profiling progress
  tableProgress: Record<string, TableProgress>;
  tablesComplete: number;
  tablesTotal: number;
  aggregateScore: number;

  // Results
  results: ProfileResults | null;
  dqAssessment: DQAssessment | null;
  relationships: RelationshipResults | null;

  // UI state
  selectedTable: string | null;
  selectTable: (tableName: string | null) => void;
  activeDetailTab: DetailTab;
  setActiveDetailTab: (tab: DetailTab) => void;
  activeView: ViewType;
  setActiveView: (view: ViewType) => void;

  // Filtering & sorting
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  activeSeverities: SeverityLevel[];
  toggleSeverity: (s: SeverityLevel) => void;
  sortField: SortField;
  sortDirection: SortDirection;
  setSorting: (field: SortField, direction: SortDirection) => void;
  density: DensityMode;
  setDensity: (mode: DensityMode) => void;

  // Computed
  filteredTables: TableProfile[];
  severityCounts: SeverityCounts;
  completedStages: SessionStage[];

  // SSE
  sseConnectionState: SSEConnectionState;
  reconnectSSE: () => void;

  // Manual refresh of results + dqAssessment from the backend — used
  // after admin-gated mutations like the AI-insights endpoint that
  // append findings server-side and need a UI re-read to surface them.
  refreshResults: (sessionId: string) => Promise<void>;

  // A11y
  announcement: string;
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useProfilerSession({
  projectId,
  resumeSessionId,
}: UseProfilerSessionOptions = {}): UseProfilerSessionReturn {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const hasResumed = useRef(false);
  // Always-current run options for the awaited-chain handlers below (uploadFile/startProfiling are not re-created when
  // runConfig changes, so reading state.runConfig inside them would be stale).
  const runConfigRef = useRef(state.runConfig);
  runConfigRef.current = state.runConfig;

  // Always-current session id — used so that callbacks (e.g. startProfiling)
  // that run in awaited-chain handlers don't read a stale closure from the
  // previous render. React state isn't synchronous, so when handleDbConnect
  // calls `await session.connectDatabase(...); await session.startProfiling()`,
  // the `startProfiling` reference was created with the OLD state where
  // sessionId was still null. Its `if (!state.sessionId) return` guard
  // would fire silently and the profile would never start.
  const sessionIdRef = useRef<string | null>(null);
  sessionIdRef.current = state.sessionId;

  // The saved project connection a database session profiles. The Rules tab runs
  // rules on THIS source, not merely the project's newest one (#844). It is
  // remembered WITH the session it belongs to and only reported while that
  // session is the current one, so any new session (an upload, a resumed
  // session, a clear) drops it without each of those paths having to remember to.
  const [profiledConnection, setProfiledConnection] = useState<{
    sessionId: string;
    connectionId: string;
  } | null>(null);
  const profiledConnectionId =
    profiledConnection && profiledConnection.sessionId === state.sessionId
      ? profiledConnection.connectionId
      : null;

  // Mirror of tablesTotal so callbacks (startProfiling, backstop poll) can
  // read the latest known total without re-binding on every state tick.
  // Without this, startProfiling's `SESSION_CREATED` dispatch would reset
  // the counter denominator to 1 because `state.tablesTotal` inside the
  // closure was captured on the connecting render (before dbConnect
  // returned tables_detected=18), yielding "18 of 1 tables profiled" mid-run.
  const tablesTotalRef = useRef<number>(0);
  tablesTotalRef.current = state.tablesTotal;

  // ── SSE stream ──────────────────────────────────────────────────────────
  const sseUrl = state.sessionId
    ? profilerApi.getProgressURL(state.sessionId)
    : "";

  const handleSSEEvent = useCallback(
    (event: SSEEvent) => {
      switch (event.type) {
        case "table.profile_start":
          dispatch({ type: "TABLE_START", tableName: event.tableName });
          break;
        case "table.profile_complete":
          dispatch({
            type: "TABLE_COMPLETE",
            tableName: event.tableName,
            score: event.score,
            severity: event.severity,
            columnCount: event.columnCount,
            rowCount: event.rowCount,
          });
          break;
        case "table.profile_error":
          // tableName "_session" is the backend's sentinel for a whole-run
          // failure (router.py's SSE generator uses it for both a genuine
          // status=="failed"/"error" and an exception inside the generator
          // itself), not a single table's error — dispatching TABLE_ERROR
          // for it left internalPhase stuck at "profiling" forever, since
          // that reducer branch only ever touches tableProgress. SET_ERROR
          // is the action that actually flips internalPhase to "error" so
          // the UI can leave the profiling spinner.
          if (event.tableName === "_session") {
            // See sessionError.ts: a run that failed keeps Retry, an
            // access-revoked closing frame drops it, and a "could not
            // re-verify" frame is ignored (the run is fine; the stream hook
            // reconnects and the /status backstop keeps polling).
            const outcome = sessionErrorOutcome(event);
            if (outcome.kind === "error") {
              dispatch({
                type: "SET_ERROR",
                error: event.error,
                retryable: outcome.retryable,
              });
            }
          } else {
            dispatch({
              type: "TABLE_ERROR",
              tableName: event.tableName,
              error: event.error,
              retryable: event.retryable,
            });
          }
          break;
        case "session.complete":
          dispatch({
            type: "SESSION_COMPLETE",
            aggregateScore: event.aggregateScore,
            tablesProfiled: event.tablesProfiled,
          });
          // Fetch full results after session completes
          fetchResults(state.sessionId!);
          break;
      }
    },
    [state.sessionId],
  );

  const { connectionState: sseConnectionState, reconnect: reconnectSSE } =
    useSSEStream({
      url: sseUrl,
      enabled: state.internalPhase === "profiling" && !!state.sessionId,
      onEvent: handleSSEEvent,
    });

  // ── Progress animation ticker ───────────────────────────────────────────
  useEffect(() => {
    if (state.internalPhase !== "profiling") return;
    const interval = setInterval(
      () => dispatch({ type: "TICK_PROGRESS" }),
      1000,
    );
    return () => clearInterval(interval);
  }, [state.internalPhase]);

  // ── Resume existing session ─────────────────────────────────────────────
  useEffect(() => {
    if (resumeSessionId && !hasResumed.current) {
      hasResumed.current = true;
      resumeSession(resumeSessionId);
    }
  }, [resumeSessionId]);

  // ── Results backstop ────────────────────────────────────────────────────
  // If the SSE stream misses the terminal `session.complete` event (race
  // where the backend finishes before the browser connects, or a proxy
  // swallows the last frame), the ViewToggleBar never renders — because it
  // depends on `state.results` which is only populated via `fetchResults()`
  // inside the SSE handler. This effect polls `/status` once all in-progress
  // table cards have completed; if the session is terminal, it fetches
  // results and unblocks the UI. Cheap when unneeded (guarded by sessionId
  // and `!state.results`).
  const resultsBackstopFired = useRef(false);
  useEffect(() => {
    if (!state.sessionId) return;
    if (state.results) return; // already loaded
    // Fire backstop in any non-terminal phase — covers the race where
    // internalPhase hasn't yet flipped to 'profiling' but the backend has
    // already completed and SSE missed the session.complete event.
    if (state.internalPhase === "loaded" || state.internalPhase === "error")
      return;
    if (resultsBackstopFired.current) return;

    // Don't wait for SSE events to tell us the run is done — the event
    // can be lost (proxy buffering, browser tab coming back from sleep,
    // reconnect races). Instead, poll /status every 2s while in the
    // profiling phase. Once it reports completed/complete/failed, we
    // fetch results ourselves.
    let cancelled = false;
    const interval = setInterval(async () => {
      if (cancelled || !state.sessionId) return;
      try {
        const status = await profilerApi.getStatus(state.sessionId);
        // Drive the "N of M tables" counter + climbing score from the poll
        // itself — this is the reliable, SSE-independent progress channel.
        // The reducer clamps monotonically, so this is safe even if SSE is
        // also delivering (they converge). Without this the counter has no
        // data source when the SSE stream fails to open/deliver and stays at
        // 0 until the completion backfill.
        if (typeof status.tables_profiled === "number" && !cancelled) {
          dispatch({
            type: "POLL_PROGRESS",
            tablesProfiled: status.tables_profiled,
            aggregateScore:
              typeof status.aggregate_score === "number"
                ? status.aggregate_score
                : 0,
          });
        }
        if (status.status === "complete" || status.status === "completed") {
          if (resultsBackstopFired.current) return;
          resultsBackstopFired.current = true;
          clearInterval(interval);
          await fetchResults(state.sessionId);
        } else if (status.status === "failed") {
          // Without this, a run that fails server-side (and whose SSE
          // table.profile_error event is lost the same way session.complete
          // can be) left the UI spinning in "profiling" forever — nothing
          // else in this poll loop ever leaves that phase.
          if (resultsBackstopFired.current) return;
          resultsBackstopFired.current = true;
          clearInterval(interval);
          dispatch({
            type: "SET_ERROR",
            error: status.error || "Profiling failed",
          });
        }
      } catch {
        // best effort
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [state.sessionId, state.results, state.internalPhase]);

  // Reset the backstop guard when a new session starts.
  useEffect(() => {
    if (!state.sessionId) {
      resultsBackstopFired.current = false;
    }
  }, [state.sessionId]);

  // ── Persist table picker state to sessionStorage ─────────────────────
  // So a mid-flow refresh (or accidental nav-back) doesn't drop the
  // user's selections. Keyed by sessionId; cleared on CLEAR_SESSION.
  const PICKER_STORAGE_PREFIX = "ztd-profiler-picker:";
  const pickerHydratedRef = useRef<string | null>(null);

  useEffect(() => {
    if (state.internalPhase !== "table_select") return;
    if (!state.sessionId) return;
    if (pickerHydratedRef.current === state.sessionId) return;
    pickerHydratedRef.current = state.sessionId;
    try {
      const raw = sessionStorage.getItem(
        PICKER_STORAGE_PREFIX + state.sessionId,
      );
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        selected?: string[];
        runConfig?: Partial<RunConfig>;
      };
      if (Array.isArray(parsed.selected) && parsed.selected.length > 0) {
        // Replace the default-all selection with the persisted one.
        dispatch({ type: "SET_ALL_TABLES", selected: false });
        for (const n of parsed.selected) {
          dispatch({ type: "TOGGLE_TABLE", tableName: n });
        }
      }
      if (parsed.runConfig) {
        dispatch({ type: "SET_RUN_CONFIG", config: parsed.runConfig });
      }
    } catch {
      // best effort — bad payload is non-fatal
    }
  }, [state.internalPhase, state.sessionId]);

  useEffect(() => {
    if (state.internalPhase !== "table_select") return;
    if (!state.sessionId) return;
    try {
      sessionStorage.setItem(
        PICKER_STORAGE_PREFIX + state.sessionId,
        JSON.stringify({
          selected: Array.from(state.selectedTableNames),
          runConfig: state.runConfig,
        }),
      );
    } catch {
      // sessionStorage may be unavailable (private mode); ignore.
    }
  }, [
    state.internalPhase,
    state.sessionId,
    state.selectedTableNames,
    state.runConfig,
  ]);

  // ── API actions ─────────────────────────────────────────────────────────

  const fetchResults = useCallback(async (sessionId: string) => {
    try {
      const [results, dq, rels] = await Promise.all([
        profilerApi.getResults(sessionId),
        profilerApi.getDQ(sessionId).catch(() => null),
        profilerApi.getRelationships(sessionId).catch(() => null),
      ]);
      dispatch({ type: "RESULTS_LOADED", results, dq, relationships: rels });
    } catch (err) {
      dispatch({
        type: "SET_ERROR",
        error: err instanceof Error ? err.message : "Failed to load results",
      });
    }
  }, []);

  const resumeSession = useCallback(
    async (sessionId: string) => {
      // Deep-linking via ?session= (e.g. "Review" from a past session card)
      // mounts with internalPhase="empty" (the INITIAL_STATE default) until
      // this async chain resolves. Without an immediate isLoading dispatch,
      // EmptySessionState — gated on `!isLoading` — renders "Connect a data
      // source" for that whole window, which flashes before the real
      // results appear and reads as if the click did nothing / started
      // fresh. START_CONNECT (isLoading: true) is the same dispatch
      // startProfiling/connectDatabase already use for their own initial
      // spinner, reused here rather than inventing a new phase.
      dispatch({ type: "START_CONNECT" });
      try {
        const status = await profilerApi.getStatus(sessionId);
        sessionIdRef.current = sessionId;
        dispatch({
          type: "SESSION_CREATED",
          sessionId,
          sourceName: status.current_table || "Resumed session",
          sourceType: "unknown",
          tablesTotal: status.tables_total,
        });

        // Accept both 'complete' and 'completed' — the backend normalizes to
        // 'completed', older records may use 'complete'. Either is terminal.
        if (status.status === "complete" || status.status === "completed") {
          await fetchResults(sessionId);
        } else if (status.status === "failed") {
          dispatch({
            type: "SET_ERROR",
            error: status.error || "Session failed",
          });
        } else {
          // Still in progress — start streaming
          dispatch({ type: "PROFILING_STARTED" });
        }
      } catch (err) {
        dispatch({
          type: "SET_ERROR",
          error:
            err instanceof Error ? err.message : "Failed to resume session",
        });
      }
    },
    [fetchResults],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      dispatch({ type: "START_UPLOAD" });
      let result: UploadResult;
      try {
        result = await profilerApi.uploadFile(file, projectId);
      } catch (err) {
        dispatch({
          type: "SET_ERROR",
          error: err instanceof Error ? err.message : "Upload failed",
        });
        return;
      }
      sessionIdRef.current = result.session_id;
      dispatch({
        type: "SESSION_CREATED",
        sessionId: result.session_id,
        sourceName: result.file_name,
        sourceType: result.source_type,
        tablesTotal: result.tables_detected,
      });

      // Auto-start profiling after upload
      const startTheRun = async (): Promise<void> => {
        const runStatus = await profilerApi.startRun({
          source_id: result.session_id,
          project_id: projectId,
          detect_pii: true,
          assess_quality: true,
          detect_relationships: true,
          detect_keys: true,
          deep_relationships: runConfigRef.current.includeDeepRelationships,
        });
        dispatch({
          type: "SESSION_CREATED",
          sessionId: runStatus.session_id,
          sourceName: result.file_name || result.source_name || file.name,
          sourceType: result.source_type,
          // Preserve any prior known total (e.g. from dbConnect.tables_detected
          // or file pre-flight) instead of clobbering it with `|| 1` when the
          // /run response hasn't computed tables_total yet.
          tablesTotal:
            runStatus.tables_total ||
            runStatus.tables_profiled ||
            tablesTotalRef.current ||
            1,
        });

        if (
          runStatus.status === "complete" ||
          runStatus.status === "completed"
        ) {
          // Profiling completed synchronously — skip SSE, fetch results directly
          dispatch({ type: "PROFILING_STARTED" });
          dispatch({
            type: "SESSION_COMPLETE",
            aggregateScore: runStatus.aggregate_score ?? 0,
            tablesProfiled:
              runStatus.tables_profiled ?? runStatus.tables_total ?? 1,
          });
          await fetchResults(runStatus.session_id);
        } else {
          // Still in progress — SSE will stream updates
          dispatch({ type: "PROFILING_STARTED" });
        }
      };

      try {
        await startTheRun();
      } catch (err) {
        await handleStartRunError(
          err,
          result.session_id,
          sessionIdRef,
          dispatch,
          startTheRun,
        );
      }
    },
    [projectId, fetchResults],
  );

  /**
   * Upload multiple files (loose selection or a folder pick) — sent
   * directly as multipart/form-data via profilerApi.uploadFiles, which
   * routes through the same /upload-folder pre-flight flow once the
   * backend has built a ZIP from them server-side.
   *
   * This used to build that ZIP client-side with JSZip (the only way to
   * reuse the single-file /upload-folder endpoint without a new backend
   * one) — holding every selected file's full content in the browser
   * tab's own memory to build one in-memory archive. That crashes with
   * "Array buffer allocation failed" once the selection adds up to a few
   * hundred MB (a real POC-scale folder, not a pathological case). The
   * backend now has a bulk-multipart /upload-files endpoint precisely so
   * the browser never needs to hold more than one HTTP request's worth of
   * streamed bytes at a time.
   */
  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 1) return uploadFile(files[0]);
      dispatch({ type: "START_UPLOAD" });
      try {
        const result = await profilerApi.uploadFiles(
          files,
          projectId,
          (loaded, total) =>
            dispatch({
              type: "UPLOAD_PROGRESS",
              percent: Math.round((loaded / total) * 100),
            }),
        );
        dispatch({
          type: "PRE_FLIGHT_PROCESSING",
          sessionId: result.session_id,
          sourceName: result.source_name,
        });
        const { sourceName, preFlightReport } = await pollFolderPreflight(
          result.session_id,
        );
        dispatch({
          type: "PRE_FLIGHT_READY",
          sessionId: result.session_id,
          sourceName: sourceName || result.source_name,
          preFlightReport,
        });
      } catch (err) {
        dispatch({
          type: "SET_ERROR",
          error: err instanceof Error ? err.message : "Upload failed",
        });
      }
    },
    [projectId, uploadFile],
  );

  /** Upload a folder archive (ZIP) — triggers pre-flight review (FR5, DP-3.1) */
  const uploadFolder = useCallback(
    async (file: File) => {
      dispatch({ type: "START_UPLOAD" });
      try {
        const result = await profilerApi.uploadFolder(
          file,
          projectId,
          (loaded, total) =>
            dispatch({
              type: "UPLOAD_PROGRESS",
              percent: Math.round((loaded / total) * 100),
            }),
        );
        dispatch({
          type: "PRE_FLIGHT_PROCESSING",
          sessionId: result.session_id,
          sourceName: result.source_name,
        });
        const { sourceName, preFlightReport } = await pollFolderPreflight(
          result.session_id,
        );
        dispatch({
          type: "PRE_FLIGHT_READY",
          sessionId: result.session_id,
          sourceName: sourceName || result.source_name,
          preFlightReport,
        });
      } catch (err) {
        dispatch({
          type: "SET_ERROR",
          error: err instanceof Error ? err.message : "Folder upload failed",
        });
      }
    },
    [projectId],
  );

  /** Toggle a single file in/out of selection during pre-flight review */
  const toggleFileSelection = useCallback((fileId: string) => {
    dispatch({ type: "TOGGLE_FILE", fileId });
  }, []);

  /** Select or deselect all processable files */
  const setAllFilesSelected = useCallback((included: boolean) => {
    dispatch({ type: "SET_ALL_FILES", included });
  }, []);

  /** Confirm file selection and start profiling (FR7, DP-3.3) */
  const confirmFileSelection = useCallback(async () => {
    if (!state.sessionId || !state.preFlightReport) return;
    const sessionId = state.sessionId;
    dispatch({ type: "FILE_SELECTION_CONFIRMED" });

    // Start profiling with selected files. sample_rows was previously
    // never sent here — the DB-source flow's TableSelectStep already
    // lets the user pick this, but the folder flow always fell through
    // to the backend's bare default (100K rows/table) with no user
    // control at all. Reuses the same runConfig.sampleRows state DB
    // sources already write to (see the pre-flight review's own sample-
    // size preset buttons in DataProfilerPage.tsx).
    const startTheRun = async (): Promise<void> => {
      const runStatus = await profilerApi.startRun({
        source_id: sessionId,
        project_id: projectId,
        sample_rows: state.runConfig.sampleRows ?? undefined,
        detect_pii: true,
        assess_quality: true,
        detect_relationships: true,
        detect_keys: true,
        deep_relationships: runConfigRef.current.includeDeepRelationships,
      });
      dispatch({
        type: "SESSION_CREATED",
        sessionId: runStatus.session_id,
        sourceName: state.sourceName || "Folder",
        sourceType: "folder",
        // For folder/multi-file sources, each selected file becomes its own
        // table — we know that count exactly the moment the user confirms
        // selection, well before /run's response reports tables_total (which
        // starts at 0 and only catches up once profiling has begun). Prefer
        // it over the generic `|| 1` fallback so the counter reads "N of 42"
        // from the very first render instead of "N of 1" until the backend
        // corrects it mid-run.
        tablesTotal:
          runStatus.tables_total ||
          runStatus.tables_profiled ||
          state.selectedFileIds.size ||
          tablesTotalRef.current ||
          1,
      });

      if (runStatus.status === "complete" || runStatus.status === "completed") {
        dispatch({ type: "PROFILING_STARTED" });
        dispatch({
          type: "SESSION_COMPLETE",
          aggregateScore: runStatus.aggregate_score ?? 0,
          tablesProfiled: runStatus.tables_profiled ?? 1,
        });
        await fetchResults(runStatus.session_id);
      } else {
        dispatch({ type: "PROFILING_STARTED" });
      }
    };

    try {
      // Send include list to backend
      const includeIds = Array.from(state.selectedFileIds);
      await profilerApi.folderFilter({
        session_id: sessionId,
        include_file_ids: includeIds,
      });
      await startTheRun();
    } catch (err) {
      await handleStartRunError(
        err,
        sessionId,
        sessionIdRef,
        dispatch,
        startTheRun,
      );
    }
  }, [
    state.sessionId,
    state.preFlightReport,
    state.selectedFileIds,
    state.sourceName,
    fetchResults,
  ]);

  const connectDatabase = useCallback(
    async (config: Record<string, unknown>): Promise<string | null> => {
      dispatch({ type: "START_CONNECT" });
      setProfiledConnection(null);
      try {
        const result = await profilerApi.dbConnect(config);
        sessionIdRef.current = result.session_id;
        setProfiledConnection(
          result.connection_id
            ? {
                sessionId: result.session_id,
                connectionId: result.connection_id,
              }
            : null,
        );
        // Land in the table-picker step. We deliberately do NOT call
        // selectTables / startProfiling here — that happens when the
        // user confirms their picker selection via confirmTableSelection().
        dispatch({
          type: "TABLES_DISCOVERED",
          sessionId: result.session_id,
          sourceName: result.schema_name,
          sourceType: result.source_type,
          tables: result.tables,
          schema: result.schema_name,
        });
        return result.session_id;
      } catch (err) {
        dispatch({
          type: "SET_ERROR",
          error: err instanceof Error ? err.message : "Connection failed",
        });
        return null;
      }
    },
    [],
  );

  // ── Schema switch (#682) ────────────────────────────────────────────────
  // Auto-fetch the schema list once the table-picker mounts for a database
  // source — the dropdown should be populated without the page component
  // needing to know this call exists. Only fires once per session (guarded
  // by schemasLoaded, reset on CLEAR_SESSION via INITIAL_STATE).
  useEffect(() => {
    if (state.internalPhase !== "table_select") return;
    if (state.sourceType !== "database") return;
    if (state.schemasLoaded || !state.sessionId) return;
    const sessionId = state.sessionId;
    let cancelled = false;
    profilerApi
      .listSchemas(sessionId)
      .then((result) => {
        if (cancelled) return;
        dispatch({ type: "SCHEMAS_LOADED", schemas: result.schemas });
      })
      .catch(() => {
        // Best-effort — the picker still works with only the schema the
        // user already connected to if this fails (permissions, an
        // engine with no schema-enumeration support, etc).
        if (!cancelled) dispatch({ type: "SCHEMAS_LOADED", schemas: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [
    state.internalPhase,
    state.sourceType,
    state.schemasLoaded,
    state.sessionId,
  ]);

  /**
   * Switch the connected session to a different schema on the SAME live
   * connection, refreshing the discovered-tables list (#682). No-op when
   * asked to switch to the schema already active.
   */
  const switchSchema = useCallback(
    async (schemaName: string) => {
      const sessionId = sessionIdRef.current || state.sessionId;
      if (!sessionId || !schemaName) return;
      if (schemaName === state.currentSchema) return;
      dispatch({ type: "SCHEMA_SWITCH_STARTED" });
      try {
        const result = await profilerApi.selectSchema(sessionId, schemaName);
        dispatch({
          type: "SCHEMA_SWITCHED",
          schema: result.schema_name || schemaName,
          tables: result.tables,
        });
      } catch (err) {
        dispatch({
          type: "SCHEMA_SWITCH_FAILED",
          error: err instanceof Error ? err.message : "Failed to switch schema",
        });
      }
    },
    [state.sessionId, state.currentSchema],
  );

  const toggleTableSelection = useCallback((tableName: string) => {
    dispatch({ type: "TOGGLE_TABLE", tableName });
  }, []);

  const setAllTablesSelected = useCallback((selected: boolean) => {
    dispatch({ type: "SET_ALL_TABLES", selected });
  }, []);

  const setRunConfig = useCallback((config: Partial<RunConfig>) => {
    dispatch({ type: "SET_RUN_CONFIG", config });
  }, []);

  /**
   * Apply the user's table picker + run-config selection.
   * Posts /select-tables, then /run with the configured sample size + flags.
   * Returns true on success.
   */
  const confirmTableSelection = useCallback(async (): Promise<boolean> => {
    const sessionId = sessionIdRef.current || state.sessionId;
    if (!sessionId) return false;
    const tableNames = Array.from(state.selectedTableNames);
    if (tableNames.length === 0) return false;

    dispatch({ type: "TABLE_SELECTION_CONFIRMED" });
    try {
      await profilerApi.selectTables(sessionId, tableNames);
    } catch (err) {
      console.warn("selectTables failed", err);
      // Continue — backend may still profile if /run receives tables itself.
    }

    const startTheRun = async (): Promise<void> => {
      const runStatus = await profilerApi.startRun({
        source_id: sessionId,
        project_id: projectId,
        sample_rows: state.runConfig.sampleRows ?? undefined,
        detect_pii: true,
        assess_quality: state.runConfig.includeDq,
        detect_relationships: state.runConfig.includeRelationships,
        detect_keys: state.runConfig.includeKeys,
      });
      dispatch({
        type: "SESSION_CREATED",
        sessionId: runStatus.session_id,
        sourceName: state.sourceName || "Session",
        sourceType: state.sourceType || "database",
        tablesTotal:
          runStatus.tables_total ||
          runStatus.tables_profiled ||
          tableNames.length ||
          tablesTotalRef.current ||
          1,
      });
      if (runStatus.status === "complete" || runStatus.status === "completed") {
        dispatch({ type: "PROFILING_STARTED" });
        dispatch({
          type: "SESSION_COMPLETE",
          aggregateScore: runStatus.aggregate_score ?? 0,
          tablesProfiled:
            runStatus.tables_profiled ?? runStatus.tables_total ?? 1,
        });
        await fetchResults(runStatus.session_id);
      } else {
        dispatch({ type: "PROFILING_STARTED" });
      }
    };

    try {
      await startTheRun();
      return true;
    } catch (err) {
      await handleStartRunError(
        err,
        sessionId,
        sessionIdRef,
        dispatch,
        startTheRun,
      );
      return false;
    }
  }, [
    state.sessionId,
    state.selectedTableNames,
    state.runConfig,
    state.sourceName,
    state.sourceType,
    projectId,
    fetchResults,
  ]);

  const startProfiling = useCallback(
    async (explicitSessionId?: string) => {
      // Prefer explicitly-passed id (used by handleDbConnect chain to avoid
      // stale-closure misses); fall back to ref (always current), then state.
      const sessionId =
        explicitSessionId || sessionIdRef.current || state.sessionId;
      if (!sessionId) return;
      dispatch({ type: "START_CONNECT" });

      const startTheRun = async (): Promise<void> => {
        const runStatus = await profilerApi.startRun({
          source_id: sessionId,
          project_id: projectId,
          detect_pii: true,
          assess_quality: true,
          detect_relationships: true,
          detect_keys: true,
          deep_relationships: runConfigRef.current.includeDeepRelationships,
        });
        dispatch({
          type: "SESSION_CREATED",
          sessionId: runStatus.session_id,
          sourceName: state.sourceName || "Session",
          sourceType: state.sourceType || "unknown",
          // Preserve any prior known total (e.g. from dbConnect.tables_detected
          // or file pre-flight) instead of clobbering it with `|| 1` when the
          // /run response hasn't computed tables_total yet. Read from the
          // ref so the closure sees the freshest value — React state captured
          // at useCallback-creation time is stale in the awaited chain.
          tablesTotal:
            runStatus.tables_total ||
            runStatus.tables_profiled ||
            tablesTotalRef.current ||
            1,
        });

        if (
          runStatus.status === "complete" ||
          runStatus.status === "completed"
        ) {
          dispatch({ type: "PROFILING_STARTED" });
          dispatch({
            type: "SESSION_COMPLETE",
            aggregateScore: runStatus.aggregate_score ?? 0,
            tablesProfiled: runStatus.tables_profiled ?? 1,
          });
          await fetchResults(runStatus.session_id);
        } else {
          dispatch({ type: "PROFILING_STARTED" });
        }
      };

      try {
        await startTheRun();
      } catch (err) {
        await handleStartRunError(
          err,
          sessionId,
          sessionIdRef,
          dispatch,
          startTheRun,
        );
      }
    },
    [state.sessionId, state.sourceName, state.sourceType, fetchResults],
  );

  const clearSession = useCallback(() => {
    dispatch({ type: "CLEAR_SESSION" });
    setProfiledConnection(null);
  }, []);

  // ── UI dispatch helpers ─────────────────────────────────────────────────

  const selectTable = useCallback(
    (tableName: string | null) => dispatch({ type: "SELECT_TABLE", tableName }),
    [],
  );
  const setActiveDetailTab = useCallback(
    (tab: DetailTab) => dispatch({ type: "SET_DETAIL_TAB", tab }),
    [],
  );
  const setActiveView = useCallback(
    (view: ViewType) => dispatch({ type: "SET_VIEW", view }),
    [],
  );
  const setSearchQuery = useCallback(
    (query: string) => dispatch({ type: "SET_SEARCH", query }),
    [],
  );
  const toggleSeverity = useCallback(
    (severity: SeverityLevel) =>
      dispatch({ type: "TOGGLE_SEVERITY", severity }),
    [],
  );
  const setSorting = useCallback(
    (field: SortField, direction: SortDirection) =>
      dispatch({ type: "SET_SORTING", field, direction }),
    [],
  );
  const setDensity = useCallback(
    (mode: DensityMode) => dispatch({ type: "SET_DENSITY", mode }),
    [],
  );
  const setStage = useCallback(
    (stage: SessionStage) => dispatch({ type: "SET_STAGE", stage }),
    [],
  );

  // ── Computed values ─────────────────────────────────────────────────────

  const tablesComplete = useMemo(
    () =>
      // Whichever source is further ahead wins: SSE per-table events (which
      // also carry the table names for the live cards) OR the `/status` poll
      // fallback. When SSE is delivering they agree; when SSE is silent the
      // poll count keeps the counter climbing.
      Math.max(
        Object.values(state.tableProgress).filter(
          (t) => t.status === "complete",
        ).length,
        state.polledTablesProfiled,
      ),
    [state.tableProgress, state.polledTablesProfiled],
  );

  const aggregateScore = useMemo(() => {
    if (state.results) return state.results.aggregate_score;
    const completed = Object.values(state.tableProgress).filter(
      (t) => t.status === "complete" && t.score != null,
    );
    // Prefer the SSE-derived running mean when SSE has delivered table
    // scores; otherwise fall back to the poll-derived aggregate so the score
    // still climbs when the stream is silent.
    if (completed.length === 0) return state.polledAggregateScore;
    return Math.round(
      completed.reduce((sum, t) => sum + (t.score || 0), 0) / completed.length,
    );
  }, [state.results, state.tableProgress, state.polledAggregateScore]);

  const severityCounts = useMemo<SeverityCounts>(() => {
    const counts: SeverityCounts = {
      excellent: 0,
      good: 0,
      moderate: 0,
      poor: 0,
      warning: 0,
      critical: 0,
    };
    const tables = state.results?.tables || [];
    if (tables.length > 0) {
      // Final results loaded — authoritative.
      for (const t of tables) {
        const sev = t.severity as SeverityLevel;
        if (sev in counts) counts[sev]++;
      }
      return counts;
    }
    // Run in progress (no results yet) → tally live from streamed per-table
    // progress so the badge climbs as tables are assessed. Tables still
    // "pending" (sampled, not yet scored) have no severity and are skipped.
    for (const tp of Object.values(state.tableProgress)) {
      const sev = tp.severity;
      if (sev && sev in counts) counts[sev as SeverityLevel]++;
    }
    return counts;
  }, [state.results, state.tableProgress]);

  const filteredTables = useMemo(() => {
    let tables = state.results?.tables || [];

    // Filter by severity
    tables = tables.filter((t) =>
      state.activeSeverities.includes(t.severity as SeverityLevel),
    );

    // Filter by search query
    if (state.searchQuery.trim()) {
      const q = state.searchQuery.toLowerCase();
      tables = tables.filter((t) => t.table_name.toLowerCase().includes(q));
    }

    // Sort
    tables = [...tables].sort((a, b) => {
      const dir = state.sortDirection === "asc" ? 1 : -1;
      switch (state.sortField) {
        case "score":
          return (a.score - b.score) * dir;
        case "name":
          return a.table_name.localeCompare(b.table_name) * dir;
        case "columns":
          return (a.column_count - b.column_count) * dir;
        case "rows":
          return (a.row_count - b.row_count) * dir;
        default:
          return 0;
      }
    });

    return tables;
  }, [
    state.results,
    state.activeSeverities,
    state.searchQuery,
    state.sortField,
    state.sortDirection,
  ]);

  const completedStages = useMemo<SessionStage[]>(() => {
    const stages: SessionStage[] = [];
    if (state.internalPhase !== "empty") stages.push("connect");
    if (state.results) stages.push("discovery");
    // Once discovery has results, "Share & Act" is reachable (export/share is
    // available). Mark it navigable so the breadcrumb can jump there — it was
    // previously never added, leaving Share reachable only via the Export
    // button. "validate" is intentionally omitted: it has no screen yet and is
    // hidden from the breadcrumb (see DataProfilerPage hideStages).
    if (state.results) stages.push("share");
    return stages;
  }, [state.internalPhase, state.results]);

  // ── Return ──────────────────────────────────────────────────────────────

  return {
    stage: state.stage,
    internalPhase: state.internalPhase,
    sessionId: state.sessionId,
    sourceName: state.sourceName,
    sourceType: state.sourceType,
    error: state.error,
    errorRetryable: state.errorRetryable,
    isLoading: state.isLoading,
    scanPending: state.scanPending,
    scanMessage: state.scanMessage,
    uploadProgress: state.uploadProgress,
    profiledConnectionId,

    uploadFile,
    uploadFiles,
    uploadFolder,
    connectDatabase,
    startProfiling,
    clearSession,
    setStage,

    preFlightReport: state.preFlightReport,
    selectedFileIds: state.selectedFileIds,
    toggleFileSelection,
    setAllFilesSelected,
    confirmFileSelection,

    discoveredTables: state.discoveredTables,
    selectedTableNames: state.selectedTableNames,
    toggleTableSelection,
    setAllTablesSelected,
    runConfig: state.runConfig,
    setRunConfig,
    confirmTableSelection,

    currentSchema: state.currentSchema,
    availableSchemas: state.availableSchemas,
    isSwitchingSchema: state.isSwitchingSchema,
    schemaSwitchError: state.schemaSwitchError,
    switchSchema,

    tableProgress: state.tableProgress,
    tablesComplete,
    tablesTotal: state.tablesTotal,
    aggregateScore,

    results: state.results,
    dqAssessment: state.dqAssessment,
    relationships: state.relationships,

    selectedTable: state.selectedTable,
    selectTable,
    activeDetailTab: state.activeDetailTab,
    setActiveDetailTab,
    activeView: state.activeView,
    setActiveView,

    searchQuery: state.searchQuery,
    setSearchQuery,
    activeSeverities: state.activeSeverities,
    toggleSeverity,
    sortField: state.sortField,
    sortDirection: state.sortDirection,
    setSorting,
    density: state.density,
    setDensity,

    filteredTables,
    severityCounts,
    completedStages,

    sseConnectionState,
    reconnectSSE,

    // Exposed so the Summary panel's "Generate AI Insights" button can
    // refresh dqAssessment after a successful POST. Avoids adding a
    // bespoke action / reducer path for the one consumer.
    refreshResults: fetchResults,

    announcement: state.announcement,
  };
}
